import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, ArrowRight, Phone, Mail, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import webDesignHeroBg from "@assets/image_1773678134341.webp";
import artisansMontage from "../assets/artisans-website-showcase.webp";
import excoScreenshot from "@assets/image_1776846719970.webp";

export default function WebDesign() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const features = [
    {
      title: "User Friendly Frameworks/CMS",
      description: "Websites are naturally tricky. Visitors should love your site for its user-friendliness. We will develop your site to increase its learnability, efficiency and visitor satisfaction. We will make sure that everything runs smoothly for everyone who visits your website, improve site navigation and speed it up, and ensure that everything is readable."
    },
    {
      title: "Responsive Website Design",
      description: "Viewing a website from a desktop compared with a mobile device is a completely different online experience. A responsive web design cuts the development costs of the desktop and mobile version of a website. Internet traffic from mobile searches is high. RWD gives a prime website viewing experience for site visitors and unique designs give identity to your site."
    },
    {
      title: "Device Compatibility",
      description: "For your site to enhance your Internet presence it should be unique and device responsive. The Cornwall SEO Co is proud to create aesthetically appealing and responsive websites, delivering a smooth online experience when using Android, iOS, Linux, MacOSX and Windows devices."
    },
    {
      title: "URL Optimisation",
      description: "A clean and optimised URL can increase the usability of a website. As a result, search engines will be able to better analyse your website and reward your site with a better search engine ranking."
    },
    {
      title: "Competitor Analysis",
      description: "We analyse competitor websites to understand current design trends and best practices, ensuring your website stands out while meeting modern web standards and user expectations."
    },
    {
      title: "Design Efficiency",
      description: "Most Internet users have a limited attention span, demanding on-site efficiency. Your site should not only be unique but also designed effectively. We ensure safe and efficient designs with auto-responder, anti-spam features and security features."
    }
  ];

  return (
    <div className="min-h-screen bg-background font-sans overflow-x-hidden">
      <Helmet>
        <title>Web Design St Austell | The Cornwall SEO Co</title>
        <meta name="description" content="Professional web design and development tailored to your business needs and user expectations. We create responsive, efficient, and user-friendly websites." />
      </Helmet>
      <Navbar />

      <section 
        className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-20 bg-black bg-cover bg-center"
        style={{ backgroundImage: `url(${webDesignHeroBg})` }}
      >
        <div className="absolute inset-0 bg-black/60 z-0"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-5xl md:text-7xl font-heading font-black text-white mb-8 leading-none uppercase italic tracking-tighter">
              Web <br/>
              <span className="text-primary">Design</span>
            </h1>
            <p className="text-xl text-white/90 mb-10 leading-relaxed font-medium">
              Professional web design and development tailored to your business needs and user expectations.
            </p>
            <a href="#contact-form" className="inline-block">
              <Button size="lg" className="bg-primary text-black hover:bg-white text-lg px-10 h-16 rounded-none font-black uppercase italic transition-all cursor-pointer">
                Contact Us <ArrowRight className="ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* USP Section */}
      <section className="py-10 bg-black border-y border-primary/20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center divide-y md:divide-y-0 md:divide-x divide-primary/20">
            <div className="text-white font-black italic text-2xl py-4 md:py-0 px-4">100% CORNWALL-BASED TEAM</div>
            <div className="text-white font-black italic text-2xl py-4 md:py-0 px-4">NO MINIMUM TERM CONTRACT</div>
            <div className="text-white font-black italic text-2xl py-4 md:py-0 px-4">COMPETITIVE HOURLY RATE</div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="w-full px-0">
          <div className="grid md:grid-cols-2 gap-16 items-center px-6">
            <motion.div {...fadeIn}>
              <h2 className="text-4xl md:text-6xl font-heading font-black text-black mb-6 uppercase italic tracking-tighter">
                Build Your <br/> <span className="text-primary">Digital Presence</span>
              </h2>
              <p className="text-black text-xl font-medium leading-relaxed mb-6">
                Whether you run a small or large business, developing your own website is essential to the greater exposure of your brand, expanding on the geographical limits of your company.
              </p>
              <p className="text-black text-lg md:text-xl font-medium leading-relaxed mb-6">
                Many companies use advertising and marketing strategies to gain traffic but are these enough? A website should be designed and built depending on the requirements, not just of the business but also of the user. A unique website, delivering the ultimate visiting experience to prospect clients, is vital.
              </p>
              <p className="text-black text-lg md:text-xl font-medium leading-relaxed">
                The Cornwall SEO Co creates, designs and develops made to measure websites that are both appealing to search engines and humans alike. Experienced in all aspects of web design and development, including SEO, URL optimisation, device responsiveness and more, we can confidently align your website so that it is search engine conscientious.
              </p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative lg:justify-self-start"
            >
              <img 
                src={artisansMontage} 
                alt="Artisans of Tuscany website design showcase" 
                className="w-full rounded-lg shadow-2xl"
              />
              <a 
                href="https://artisansoftuscany.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="absolute -bottom-4 right-0 md:-right-4 bg-primary text-black font-black p-4 rounded-lg shadow-lg max-w-xs text-sm uppercase italic hover:bg-white hover:text-black transition-all inline-block"
              >
                <span className="text-white">🔗</span> VISIT LIVE SITE
              </a>
            </motion.div>
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-16 pt-16 border-t-2 border-primary"
          >
            <div className="max-w-3xl mx-auto px-4 md:px-0">
              <p className="text-black text-base md:text-lg font-medium leading-relaxed mb-8 italic">
                "I recently engaged Page1 for a web design project and was thoroughly impressed by their professionalism. Their workflow is incredibly efficient, ensuring the project stayed on track without any delays. The level of communication by Justin Clarke was outstanding; they were proactive in providing updates and were always available to discuss ideas or concerns and assist with tech help. Most importantly, the end result was exactly what I hoped for. I highly recommend their services to anyone looking for a reliable and talented web design partner."
              </p>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <p className="text-black font-black uppercase italic text-base md:text-lg">Andrew Reed</p>
                  <p className="text-black font-black uppercase italic text-primary text-sm md:text-base">Artisans of Tuscany</p>
                </div>
                <div className="w-12 h-12 bg-primary flex items-center justify-center shrink-0">
                  <span className="text-2xl">✓</span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-16 pt-16 border-t-2 border-primary"
          >
            <div className="w-full px-6">
              <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center w-full">
                <div className="bg-black text-white p-8 md:p-10 border-l-8 border-primary shadow-2xl">
                  <p className="text-primary font-black uppercase italic text-sm mb-4 tracking-widest">Recently Launched April 2026</p>
                  <h3 className="text-3xl md:text-5xl font-heading font-black uppercase italic mb-6 tracking-tighter text-white">EXCO Fire & Security</h3>
                  <p className="text-white/90 text-base md:text-lg font-medium leading-relaxed mb-8">
                    We recently launched a new website for EXCO Fire & Security, creating a clean and confident online presence that supports their professional service offering.
                  </p>
                  <p className="text-white text-base md:text-lg font-medium leading-relaxed italic">
                    "Justin did an amazing job designing my website. He really listened to what I wanted and turned my ideas into something even better than I imagined. I’m genuinely happy with the result and highly recommend him."<br /><br />
                    <span className="font-black uppercase not-italic">Ian Priest</span><br />
                    <span className="font-black uppercase not-italic text-white">EXCO Fire & Security</span>
                  </p>
                </div>
                <div className="relative lg:justify-self-start">
                  <img
                    src={excoScreenshot}
                    alt="EXCO Fire & Security website screenshot"
                    className="w-full h-[420px] object-cover object-[left_center] rounded-lg shadow-2xl"
                  />
                  <a 
                    href="https://excotec.co.uk" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="absolute -bottom-4 right-0 md:-right-4 bg-primary text-black font-black p-4 rounded-lg shadow-lg max-w-xs text-sm uppercase italic hover:bg-white hover:text-black transition-all inline-block"
                  >
                    <span className="text-white">🔗</span> VISIT LIVE SITE
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full rounded-none border-2 border-black p-6">
                  <CardHeader className="p-0 mb-4">
                    <CardTitle className="font-heading font-black text-xl md:text-xl text-black uppercase italic">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <p className="text-black font-medium leading-relaxed text-lg md:text-sm">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact-form" className="py-12 md:py-24 bg-primary">
        <div className="container mx-auto px-4 md:px-6">
          <div className="bg-black p-4 md:p-20 shadow-[10px_10px_0px_0px_rgba(255,255,255,0.3)] md:shadow-[20px_20px_0px_0px_rgba(255,255,255,0.3)] overflow-x-hidden">
            <div className="grid md:grid-cols-2 gap-4 md:gap-16">
              <div>
                <h3 className="text-3xl md:text-6xl font-heading font-black text-primary mb-4 md:mb-6 uppercase italic tracking-tighter">Ready to Launch <br/> Your Site?</h3>
                <p className="text-white/80 text-base md:text-xl mb-6 md:mb-12 font-medium">
                  Let's create a beautiful, functional website that attracts visitors and converts them into customers.
                </p>
                
                <div className="space-y-4 md:space-y-8">
                   <div className="flex items-center gap-3 md:gap-6">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary flex items-center justify-center shrink-0">
                         <Phone size={18} className="text-black md:w-6 md:h-6" />
                      </div>
                      <span className="text-white font-black uppercase italic text-sm md:text-xl break-words">01726 74161</span>
                   </div>
                   <div className="flex items-center gap-3 md:gap-6">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary flex items-center justify-center shrink-0">
                         <Mail size={18} className="text-black md:w-6 md:h-6" />
                      </div>
                      <a href="mailto:hello@thecornwallseoco.co.uk" className="text-white font-black uppercase italic text-xs md:text-xl hover:text-primary transition-colors break-words">hello@thecornwallseoco.co.uk</a>
                   </div>
                   <a
                     href="https://wa.me/447792646061"
                     target="_blank"
                     rel="noopener noreferrer"
                     className="flex items-center gap-3 md:gap-6 hover:text-primary transition-colors"
                   >
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary flex items-center justify-center shrink-0">
                         <MessageCircle size={18} className="text-black md:w-6 md:h-6" />
                      </div>
                      <span className="text-white font-black uppercase italic text-sm md:text-xl hover:text-primary transition-colors">WhatsApp</span>
                   </a>
                </div>
              </div>
              
              <form action="/contact.php" method="POST" className="space-y-3 md:space-y-4">
                <Input name="name" required placeholder="NAME" className="bg-white border-none rounded-none h-12 md:h-14 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Input name="email" required type="email" placeholder="EMAIL" className="bg-white border-none rounded-none h-12 md:h-14 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Input name="website" placeholder="WEBSITE" className="bg-white border-none rounded-none h-12 md:h-14 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Textarea name="message" required placeholder="HOW CAN WE HELP YOU?" className="bg-white border-none rounded-none h-24 md:h-32 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Button type="submit" className="w-full bg-primary text-black hover:bg-white h-14 md:h-16 font-black uppercase italic text-lg md:text-xl rounded-none transition-all cursor-pointer">
                  GO
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
